import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { PackageAssetTypesQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { useState } from 'react'
import { PackageAssetTypeFilters } from '@/types'
import { useTable } from '@/helpers/useTable'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { NewDialog } from './new_dialog'
import { DeleteDialog } from '../delete_dialog'
import { Mutation as DeleteMutation } from './delete_mutation'

interface Props extends TableProps {
  createable?: boolean
  deleteable?: boolean
  filters?: PackageAssetTypeFilters
}

export const Table = ({ pageSize = 20, createable = false, deleteable = false, filters = {}, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<PackageAssetTypesQuery>(
    Query,
    { variables: { filters, first: pageSize } }
  )
  const {
    pageInfo,
    openNew,
    openDelete,
    setOpenNew,
    setOpenDelete
  } = useTable({ pageInfo: data?.packageAssetTypes.pageInfo })
  const [packageAssetTypeId, setPackageAssetTypeId] = useState<string>()

  const deletePackageAssetType = (id: string) => {
    if (!deleteable) return

    setPackageAssetTypeId(id)
    setOpenDelete(true)
  }

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  return (
    <>
      <BaseTable<PackageAssetTypeFilters>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        filters={[]}
        headings={[{ name: 'Asset Type' }, { name: 'Quantity' }, { name: '' }]}
        initialFilters={filters}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.packageAssetTypes.nodes = dedupe([
              ...previousData.packageAssetTypes.nodes,
              ...fetchMoreResult.packageAssetTypes.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        rows={data.packageAssetTypes.nodes.map(p => (
          {
            id: p.id,
            columns: [
              p.assetType.name,
              p.quantity,
              deleteable
                ? <div className="text-right -mx-1">
                  <Button color="red" onClick={() => deletePackageAssetType(p.id) }>Delete</Button>
                </div>
                : <></>
            ]
          }
        ))}
      />
      { packageAssetTypeId && (<DeleteDialog
        id={packageAssetTypeId}
        mutation={DeleteMutation}
        open={openDelete}
        refetchQueries={[Query]}
        setOpen={setOpenDelete}
        onSuccess={
          () => {
            setOpenDelete(false)
            setPackageAssetTypeId(undefined)
          }
        }
      />) }
      { filters.packageIdEq &&
        <NewDialog
          defaultValues={{ packageId: filters.packageIdEq }}
          open={openNew}
          setOpen={setOpenNew}
        /> }
    </>
  )
}
